import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import PageHeader from 'components/global/PageHeader';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useModalRouter from 'hooks/useModalRouter';
import useRouterFilters from 'hooks/useRouterFilters';

import { BRANCH_USERS, ENUMS } from 'services/consts';

import { setListMapView } from 'stores/layoutSlice';

import { DYNAMICLISTS } from 'utils/constants';

import { faList, faLocationDot, faPlus } from '@fortawesome/pro-solid-svg-icons';

const BRPendingOrderList = () => {
	const defaultSort = useMemo(() => [{ id: 'createdDate', desc: true }], []);
	const listMapView = useSelector((state) => state.layout.listMapView);
	const dispatch = useDispatch();

	const columns = useMemo(
		() => [
			{
				Header: 'otoOrderID',
				accessor: 'ID',
				filterType: FilterType.number,
				width: 70,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'orderID',
				accessor: 'orderID',
				filterType: FilterType.string,
				width: 100,
				showOnMobile: true,
				showOnMap: true,
				showCopyButton: true
			},
			{
				Header: 'orderDate',
				accessor: 'createdDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'status',
				accessor: 'status',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.BR_PENDING_ORDERS_STATUS,
				renderCell: ListComponents.SHIPMENT_STATUS,
				width: 200,
				showOnMobile: true,
				showOnMap: true
			},
			{
				Header: 'salesChannel',
				accessor: 'salesChannelName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.SALES_CHANNEL_ACTIVE,
				width: 100,
				renderCell: ListComponents.SALES_CHANNEL_LOGO,
				show: false,
				roleName: 'SalesChannelModule'
			},
			{
				Header: 'branchName',
				accessor: 'branchName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.BRANCHES,
				width: 100
			},
			{
				Header: 'brandName',
				accessor: 'storeName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.BRAND_NAME,
				width: 100,
				renderCell: ListComponents.BRAND_LOGO,
				roleName: 'BrandingModule',
				show: false
			},
			{
				Header: 'customerName',
				accessor: 'name',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'customerEmail',
				accessor: 'email',
				filterType: FilterType.string,
				width: 100,
				show: false
			},
			{
				Header: 'customerPhone',
				accessor: 'phone',
				filterType: FilterType.string,
				width: 100,
				renderCell: ListComponents.PHONE,
				show: false
			},
			{
				Header: 'customerAddress',
				accessor: 'address',
				filterType: FilterType.string,
				width: 95
			},
			{
				Header: 'destinationCountry',
				accessor: 'countryName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				width: 100,
				show: false
			},
			{
				Header: 'destinationCity',
				accessor: 'cityName',
				filterType: FilterType.multiSelection,
				width: 100,
				show: false
			},
			{
				Header: 'grandTotal',
				accessor: 'grandTotal',
				filterType: FilterType.float,
				width: 100,
				renderCell: ListComponents.CURRENCY,
				showOnMap: true,
				show: false
			},
			{
				Header: 'packageCount',
				accessor: 'packageCount',
				filterType: FilterType.number,
				width: 50,
				show: false
			},
			{
				Header: 'weight',
				accessor: 'weight',
				filterType: FilterType.number,
				width: 50,
				show: false
			},
			{
				Header: 'packageSize',
				accessor: 'maxPackageSize',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'packingStatus',
				accessor: 'packingStatus',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PACKING_STATUS,
				show: false,
				roleName: ['WMSModule', 'WMSAdvanceModule']
			},
			{
				Header: 'couponCode',
				accessor: 'couponCode',
				filterType: FilterType.string,
				show: false,
				roleName: 'EnterprisePackage'
			},
			{
				Header: 'skus',
				accessor: 'skuList',
				filterType: FilterType.string,
				width: 70,
				show: false,
				roleName: 'EnterprisePackage'
			},
			{
				Header: 'quantities',
				accessor: 'qtyList',
				filterType: FilterType.number,
				width: 70,
				show: false,
				roleName: 'EnterprisePackage'
			},
			{ Header: 'district', accessor: 'district', filterType: FilterType.string, width: 100, show: false },
			{ Header: 'state', accessor: 'state', filterType: FilterType.string, width: 100, show: false },
			{ Header: 'region', accessor: 'region', filterType: FilterType.string, width: 100, show: false },
			{
				Header: 'areaName',
				accessor: 'areaName',
				filterType: FilterType.string,
				show: false,
				roleName: 'OMSModule'
			},
			{
				Header: 'paymentMethod',
				accessor: 'paymentMethod',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PAYMENT_METHOD_FOR_LIST,
				width: 100,
				showOnMap: true
			},
			{
				Header: 'totalDue',
				accessor: 'totalDue',
				filterType: FilterType.float,
				width: 90,
				show: false,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'currency',
				accessor: 'currency',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'lastComment',
				accessor: 'lastComment',
				filterType: FilterType.string,
				renderCell: ListComponents.LAST_COMMENT,
				width: 100,
				show: false,
				roleName: 'EnterprisePackage'
			},
			{ Header: 'comment', accessor: 'comments', filterType: FilterType.string, width: 120, show: false },
			{
				Header: 'deliverySlot',
				accessor: 'deliverySlot',
				filterType: FilterType.string,
				width: 100,
				show: false,
				roleName: 'EnterprisePackage'
			},
			{
				Header: 'deliverySlotDate',
				accessor: 'deliverySlotDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATE,
				width: 100,
				show: false,
				roleName: 'EnterprisePackage'
			},
			{
				Header: 'deliveryName',
				accessor: 'deliveryName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DELIVERY_COMPANIES_USED,
				width: 100,
				renderCell: ListComponents.DC_LOGO,
				show: false
			},
			{
				Header: 'awbPrinted',
				accessor: 'printed',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.YES_NO,
				renderCell: ListComponents.STATUS,
				width: 40,
				show: false,
				roleName: ['ScalePackage', 'EnterprisePackage']
			},
			{
				Header: 'shipmentCreationDate',
				accessor: 'shipmentCreationDate',
				filterType: FilterType.date,
				showOnMobile: true,
				show: false,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'ref1',
				accessor: 'ref1',
				filterType: FilterType.string,
				width: 90,
				show: false,
				roleName: '/column/ref1',
				renderCell: ListComponents.URL
			},
			{
				Header: 'ref2',
				accessor: 'ref2',
				filterType: FilterType.string,
				width: 90,
				show: false,
				roleName: '/column/ref2',
				renderCell: ListComponents.URL
			},
			{
				Header: 'ref3',
				accessor: 'ref3',
				filterType: FilterType.string,
				width: 90,
				show: false,
				roleName: '/column/ref3',
				renderCell: ListComponents.URL
			},
			{
				Header: 'orderDetailsPrinted',
				accessor: 'orderDetailsPrinted',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.YES_NO,
				renderCell: ListComponents.STATUS,
				width: 80,
				show: false,
				roleName: ['ScalePackage', 'EnterprisePackage']
			}
		],
		[]
	);

	const { showModal } = useModalRouter();

	const filtersFromRouter = useRouterFilters(columns);

	return (
		<div>
			<PageHeader
				title="pendingOrders"
				lordIcon={
					'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/pendingOrders-icon.json.gz'
				}>
				<Button
					icon={listMapView ? faList : faLocationDot}
					label={listMapView ? 'listView' : 'Mapview'}
					onClick={() => dispatch(setListMapView(!listMapView))}
					variant="borderless"
					lordIcon={
						listMapView
							? 'https://storage.googleapis.com/tryoto-public/lottie-files-compressed/listView-icon.json.gz'
							: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/mapView-icon.json.gz'
					}
					className="mobile-hidden mapViewButton"
				/>
				<Authorization path="/modal/add-order-form">
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addOrder"
						onClick={() => showModal('add-order-form')}
						variant="blue"
						className="addButton"
					/>
				</Authorization>
			</PageHeader>

			<DynamicList
				id={DYNAMICLISTS.BR_PENDING_ORDERS}
				columns={columns}
				fetchUrl={BRANCH_USERS.GET_PENDING_ORDER_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.ORDER}
				defaultFilters={filtersFromRouter}
				defaultSort={defaultSort}
				exportName="brPendingOrders"
				showMapViewButton
			/>
		</div>
	);
};

export default BRPendingOrderList;
